'use client';
import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { BackgroundColorContainer, Heading } from '@web/atoms';
import { ResponsiveImage } from '@web/molecules';
import {
  ContentContainer,
  ContactInfo,
  ContactMethod,
  IconContainer,
  Eyebrow,
  PageHeadings,
  ContactsContainer,
  ContactInfoText,
  ContactInfoTitle,
  HeadingContainer,
  Subheading,
  ContactInfoTextContainer,
  ContactsContainerText,
} from '../Gladly/styles.js';
import { GLADLY_CONFIG } from '../Gladly/config';
import {
  FaqContainer,
  FaqSection,
  FaqSectionHeader,
  FaqItem,
  FaqItemLink,
  AnswerDetail,
  AnswerContent,
  BackLink,
  SearchContainer,
  SearchInput,
} from './styles.js';

const fetchHelpCenterConfig = async () => {
  const res = await fetch(
    `${GLADLY_CONFIG.cdn}/orgs/configs/help-center/${GLADLY_CONFIG.brandId}.json`,
  );
  if (!res.ok) return null;
  return res.json();
};

const fetchAnswerTitles = async () => {
  const res = await fetch(
    `${GLADLY_CONFIG.api}/api/v1/orgs/${GLADLY_CONFIG.orgId}/help-center/${GLADLY_CONFIG.brandId}/answer-titles?lng=en-us`,
  );
  if (!res.ok) return [];
  return res.json();
};

const fetchAnswer = async answerId => {
  const res = await fetch(
    `${GLADLY_CONFIG.api}/api/v1/orgs/${GLADLY_CONFIG.orgId}/answers/${answerId}?lng=en-us`,
  );
  if (!res.ok) return null;
  return res.json();
};

export const GladlyFaqList = ({
  contactHeading,
  contactMethods,
  eyebrow,
  heading,
  subheading,
  backgroundColor = 'background.base',
}) => {
  const [sections, setSections] = useState([]);
  const [answerTitlesMap, setAnswerTitlesMap] = useState({});
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFaqData = async () => {
      try {
        const [config, answerTitles] = await Promise.all([
          fetchHelpCenterConfig(),
          fetchAnswerTitles(),
        ]);

        if (config?.sections) {
          setSections(config.sections);
        }

        if (answerTitles) {
          const titlesMap = {};
          answerTitles.forEach(answer => {
            titlesMap[answer.id] = answer;
          });
          setAnswerTitlesMap(titlesMap);
        }
      } catch (e) {
        // Fail silently — FAQ section just won't render
      } finally {
        setLoading(false);
      }
    };

    loadFaqData();
  }, []);

  const handleAnswerClick = useCallback(async answerId => {
    const answer = await fetchAnswer(answerId);
    if (answer) {
      setSelectedAnswer(answer);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const handleBack = useCallback(() => {
    setSelectedAnswer(null);
  }, []);

  const onChatButtonHandler = () => {
    const { Gladly } = window;
    if (Gladly?.show) Gladly.show();
  };

  const filteredSections = sections
    .map(section => {
      const answers = (section.answerIds || [])
        .map(id => answerTitlesMap[id])
        .filter(Boolean);

      if (!searchQuery) return { ...section, answers };

      const filtered = answers.filter(a =>
        a.name?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      return { ...section, answers: filtered };
    })
    .filter(section => section.answers.length > 0);

  return (
    <BackgroundColorContainer $bgColor={backgroundColor}>
      <ContentContainer>
        <PageHeadings>
          <Eyebrow>{eyebrow}</Eyebrow>
          <Heading
            headingText={heading}
            headingLevel="h2"
            typography="heading1"
            horizontalPadding={2}
            verticalPadding={1}
            backgroundColor={backgroundColor}
          />
        </PageHeadings>

        {selectedAnswer ? (
          <AnswerDetail>
            <BackLink onClick={handleBack}>Back to all questions</BackLink>
            <h2>{selectedAnswer.name}</h2>
            <AnswerContent
              dangerouslySetInnerHTML={{ __html: selectedAnswer.bodyHtml }}
            />
          </AnswerDetail>
        ) : (
          <>
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder="Search for answers..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </SearchContainer>

            {loading ? null : (
              <FaqContainer>
                {filteredSections.map(section => (
                  <FaqSection key={section.id}>
                    <FaqSectionHeader>{section.header}</FaqSectionHeader>
                    {section.answers.map(answer => (
                      <FaqItem key={answer.id}>
                        <FaqItemLink
                          onClick={() => handleAnswerClick(answer.id)}
                        >
                          {answer.name}
                        </FaqItemLink>
                      </FaqItem>
                    ))}
                  </FaqSection>
                ))}
              </FaqContainer>
            )}
          </>
        )}

        <ContactInfo>
          <HeadingContainer>
            <Heading
              backgroundColor={backgroundColor}
              headingText={contactHeading}
              headingLevel="h3"
              typography="heading2"
              position="center"
              verticalPadding={1}
            />
            <Subheading>{subheading}</Subheading>
          </HeadingContainer>
          <ContactsContainer>
            {contactMethods.map((contact, i) => {
              const [width, height] = [40, 40];
              return (
                <ContactMethod key={i}>
                  <IconContainer
                    href={contact.path}
                    onClick={
                      contact.title === 'Live chat with'
                        ? onChatButtonHandler
                        : null
                    }
                  >
                    <ResponsiveImage
                      src={contact.icon.src}
                      alt={contact.icon.alt}
                      id={`contact_${i}`}
                      width={width}
                      height={height}
                    />
                  </IconContainer>
                  <ContactInfoTitle>{contact.title}</ContactInfoTitle>
                  <ContactInfoTextContainer
                    onClick={
                      contact.title === 'Live chat with'
                        ? onChatButtonHandler
                        : null
                    }
                  >
                    <ContactInfoText href={contact.path}>
                      {contact.text}{' '}
                    </ContactInfoText>
                  </ContactInfoTextContainer>
                </ContactMethod>
              );
            })}
          </ContactsContainer>
          <ContactsContainerText>
            *By texting us, you consent to receive text messages from Ollie at
            the mobile number you used to text and you are opting-in to receive
            future messages or a phone call at the number you provided. Message
            & Data rates may apply. Reply STOP anytime to opt-out.
          </ContactsContainerText>
        </ContactInfo>
      </ContentContainer>
    </BackgroundColorContainer>
  );
};

GladlyFaqList.displayName = 'GladlyFaqList';
GladlyFaqList.propTypes = {
  eyebrow: PropTypes.string.isRequired,
  heading: PropTypes.string.isRequired,
  subheading: PropTypes.string,
  contactHeading: PropTypes.string.isRequired,
  contactMethods: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string,
      text: PropTypes.string.isRequired,
      icon: PropTypes.shape({
        alt: PropTypes.string.isRequired,
        src: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
          .isRequired,
      }).isRequired,
    }).isRequired,
  ),
  backgroundColor: PropTypes.string,
};
